import { forwardRef, useEffect, useMemo, useState } from 'react'
import styles from './header.module.css'
import clsx from 'clsx'
import { MeshAvatar } from '../common'
import { ReactComponent as ArrowDownIcon } from '../../assets/keyboard_arrow_down_icon.svg'
import { useMessagingService } from '../../agent'
import { HumanProxy, HumanProxyType } from '../../types'
import { Id } from '../../aliases'
import { uint8ToBase64 } from '../../utils'

type Props = {
  onClick: () => void
  isActive: boolean
  'aria-pressed'?: boolean
  humanProxyId?: Id
}

export const MenuButton = forwardRef<HTMLDivElement, Props>(
  ({ onClick, humanProxyId, isActive, ...rest }, ref) => {
    const messagingService = useMessagingService()
    const [humanProxies, setHumanProxies] = useState<HumanProxy[]>([])

    useEffect(() => {
      messagingService
        .listHumanProxies()
        .then(it => {
          const payload = it.payload
          setHumanProxies(payload?.humanProxies || [])
        })
        .catch(err => {
          console.warn('Failed to list human proxies:', err)
        })
    }, [messagingService])

    const humanProxy = useMemo(() => {
      const persona = humanProxies.find(
        it => it.type === HumanProxyType.persona,
      )

      if (!humanProxyId) return persona

      const targetId = uint8ToBase64(humanProxyId)
      const matched = humanProxies.find(
        it => uint8ToBase64(it.profile.id) === targetId,
      )
      return matched ?? persona
    }, [humanProxies, humanProxyId])
    const profile = humanProxy?.profile
    const isOrgProfile = humanProxy?.type === HumanProxyType.orgMember
    const { friendlyName, avatar, firstName, lastName } = profile || {}
    const name = !isOrgProfile
      ? friendlyName
      : [firstName, lastName].filter(Boolean).join(' ')
    const avatarUrl = avatar?.imageUrl
    return (
      <div
        ref={ref}
        className={clsx(styles.menuButton, isActive && styles.menuButtonActive)}
        onClick={onClick}
        {...rest}
      >
        <MeshAvatar imageUrl={avatarUrl} name={name} />
        <div>{name}</div>
        <ArrowDownIcon className={styles.arrowDownIcon} />
      </div>
    )
  },
)

MenuButton.displayName = 'MenuButton'
